"use client";
import { useEffect, useRef } from "react";
import gsap from "gsap";

export default function GooeyButton({
  children,
  onClick,
  className = "",
  type = "button",
  disabled = false,
  onTouchStart,
  onTouchEnd,
}: any) {
  const containerRef = useRef<HTMLDivElement>(null);
  const tlRef = useRef<gsap.core.Timeline | null>(null);

  useEffect(() => {
    if (!containerRef.current) return;

    const circlesTopLeft = containerRef.current.querySelectorAll(".circle.top-left");
    const circlesBottomRight = containerRef.current.querySelectorAll(".circle.bottom-right");
    const effectButton = containerRef.current.querySelector(".effect-button");

    const btTl = gsap.timeline({ paused: true });

    const tl = gsap.timeline();
    tl.to(circlesTopLeft, { duration: 1.2, x: -25, y: -25, scaleY: 2, ease: "power1.inOut" });
    tl.to(circlesTopLeft[0], { duration: 0.1, scale: 0.2, x: "+=6", y: "-=2" });
    tl.to(circlesTopLeft[1], { duration: 0.1, scaleX: 1, scaleY: 0.8, x: "-=10", y: "-=7" }, "-=0.1");
    tl.to(circlesTopLeft[2], { duration: 0.1, scale: 0.2, x: "-=15", y: "+=6" }, "-=0.1");
    tl.to(circlesTopLeft[0], { duration: 1, scale: 0, x: "-=5", y: "-=15", opacity: 0 });
    tl.to(circlesTopLeft[1], { duration: 1, scaleX: 0.4, scaleY: 0.4, x: "-=10", y: "-=10", opacity: 0 }, "-=1");
    tl.to(circlesTopLeft[2], { duration: 1, scale: 0, x: "-=15", y: "+=5", opacity: 0 }, "-=1");

    const tlBt1 = gsap.timeline();
    tlBt1.set(circlesTopLeft, { x: 0, y: 0, rotation: -45 });
    tlBt1.add(tl);

    const tl2 = gsap.timeline();
    tl2.set(circlesBottomRight, { x: 0, y: 0 });
    tl2.to(circlesBottomRight, { duration: 1.1, x: 30, y: 30, ease: "power1.inOut" });
    tl2.to(circlesBottomRight[0], { duration: 0.1, scale: 0.2, x: "-=6", y: "+=3" });
    tl2.to(circlesBottomRight[1], { duration: 0.1, scale: 0.8, x: "+=7", y: "+=3" }, "-=0.1");
    tl2.to(circlesBottomRight[2], { duration: 0.1, scale: 0.2, x: "+=15", y: "-=6" }, "-=0.2");
    tl2.to(circlesBottomRight[0], { duration: 1, scale: 0, x: "+=5", y: "+=15", opacity: 0 });
    tl2.to(circlesBottomRight[1], { duration: 1, scale: 0.4, x: "+=7", y: "+=7", opacity: 0 }, "-=1");
    tl2.to(circlesBottomRight[2], { duration: 1, scale: 0, x: "+=15", y: "-=5", opacity: 0 }, "-=1");

    const tlBt2 = gsap.timeline();
    tlBt2.set(circlesBottomRight, { x: 0, y: 0, rotation: 45 });
    tlBt2.add(tl2);

    btTl.add(tlBt1);
    btTl.to(effectButton, { duration: 0.8, scaleY: 1.1 }, 0.1);
    btTl.add(tlBt2, 0.2);
    btTl.to(effectButton, { duration: 1.8, scale: 1, ease: "elastic.out(1.2, 0.4)" }, 1.2);

    btTl.timeScale(2.6);
    tlRef.current = btTl;

    return () => {
      btTl.kill();
    };
  }, []);

  const handleMouseEnter = () => {
    if (tlRef.current) {
      tlRef.current.restart();
    }
  };

  return (
    <div
      className={`gooey-btn-wrapper group ${className}`}
      ref={containerRef}
      onMouseEnter={handleMouseEnter}
      onClick={onClick}
      onTouchStart={(e) => {
        handleMouseEnter();
        if (onTouchStart) onTouchStart(e);
      }}
      onTouchEnd={onTouchEnd}
    >
      <svg xmlns="http://www.w3.org/2000/svg" version="1.1" className="goo-filter">
        <defs>
          <filter id="goo">
            <feGaussianBlur in="SourceGraphic" stdDeviation="10" result="blur" />
            <feColorMatrix
              in="blur"
              mode="matrix"
              values="1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 19 -9"
              result="goo"
            />
            <feComposite in="SourceGraphic" in2="goo" />
          </filter>
        </defs>
      </svg>
      <span className="button--bubble__container w-full h-full">
        <button
          type={type}
          disabled={disabled}
          className="button button--bubble z-10 relative flex items-center justify-center w-full h-full font-bold cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {children}
        </button>
        <span className="button--bubble__effect-container">
          <span className="circle top-left"></span>
          <span className="circle top-left"></span>
          <span className="circle top-left"></span>
          <span className="button effect-button"></span>
          <span className="circle bottom-right"></span>
          <span className="circle bottom-right"></span>
          <span className="circle bottom-right"></span>
        </span>
      </span>
      <style jsx>{`
        .gooey-btn-wrapper {
          position: relative;
          display: inline-block;
        }
        .button {
          -webkit-font-smoothing: antialiased;
          background-color: transparent;
          color: #fff;
          display: inline-block;
          text-decoration: none;
          user-select: none;
          letter-spacing: 1px;
          text-transform: uppercase;
          transition: all 0.1s ease-out;
        }
        .button:active {
          transform: scale(0.95);
        }
        .button--bubble {
          position: relative;
          z-index: 2;
          background: none;
        }
        /* When hovering the wrapper, trigger color changes */
        .gooey-btn-wrapper:hover .circle,
        .gooey-btn-wrapper:hover .effect-button {
          background: #74EAF8;
        }
        
        @keyframes hue-rotate {
          from { filter: hue-rotate(0deg); }
          to { filter: hue-rotate(360deg); }
        }
        
        .gooey-btn-wrapper:hover {
          animation: hue-rotate 4s linear infinite;
        }

        .button--bubble:active + .button--bubble__effect-container {
          transform: scale(0.95);
        }
        .button--bubble__container {
          position: relative;
          display: inline-block;
        }
        .button--bubble__effect-container {
          position: absolute;
          display: block;
          width: 200%;
          height: 400%;
          top: -150%;
          left: -50%;
          filter: url("#goo");
          transition: all 0.1s ease-out;
          pointer-events: none;
        }
        .circle {
          position: absolute;
          width: 25px;
          height: 25px;
          border-radius: 15px;
          background: #000;
          transition: background 0.1s ease-out;
        }
        .circle.top-left {
          top: 40%;
          left: 27%;
        }
        .circle.bottom-right {
          bottom: 40%;
          right: 27%;
        }
        .effect-button {
          position: absolute;
          width: 50%;
          height: 25%;
          top: 50%;
          left: 25%;
          z-index: 1;
          transform: translateY(-50%);
          background: #000;
          border-radius: 100px;
          transition: background 0.1s ease-out;
        }
        .goo-filter {
          position: absolute;
          visibility: hidden;
          width: 1px;
          height: 1px;
        }
      `}</style>
    </div>
  );
}
